"use client";

import { Button } from "@repo/ui";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { authClient } from "@/lib/auth-client";

/**
 * Impersonation Banner
 *
 * Shows a fixed banner at the top of the screen when an admin is impersonating a user.
 * Only visible when session.session.impersonatedBy is set.
 * Sets --impersonation-banner-height CSS variable so other fixed elements can position below it.
 */
export function ImpersonationBanner() {
  const { data: session } = authClient.useSession();
  const bannerRef = useRef<HTMLDivElement>(null);
  const isImpersonating = Boolean(session?.session?.impersonatedBy);

  // Set CSS variable for banner height when visible
  useEffect(() => {
    if (bannerRef.current && isImpersonating) {
      const height = bannerRef.current.offsetHeight;
      document.documentElement.style.setProperty(
        "--impersonation-banner-height",
        `${height}px`,
      );
    }

    return () => {
      document.documentElement.style.setProperty(
        "--impersonation-banner-height",
        "0px",
      );
    };
  }, [isImpersonating]);

  if (!isImpersonating) {
    return null;
  }

  const handleStop = async () => {
    try {
      const { error } = await authClient.admin.stopImpersonating({});
      if (error) {
        toast.error("Failed to stop impersonating");
        return;
      }
      window.location.reload();
    } catch {
      toast.error("Failed to stop impersonating");
    }
  };

  return (
    <div
      ref={bannerRef}
      className="bg-destructive text-destructive-foreground fixed top-0 right-0 left-0 z-[60] flex items-center justify-center gap-4 px-4 py-2"
    >
      <span className="text-sm font-medium">
        Impersonating {session?.user.name} ({session?.user.email})
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void handleStop()}
        className="border-destructive-foreground text-destructive-foreground hover:bg-destructive-foreground hover:text-destructive h-7 bg-transparent"
      >
        <X className="mr-1 h-3 w-3" />
        Stop
      </Button>
    </div>
  );
}
